import { motion, useReducedMotion } from "framer-motion";

/**
 * 3D-style "stairs" for the Process section, drawn like the hero funnel:
 * one rising column per step, an app tile that climbs onto the column of
 * the currently active step, and a trophy waiting on the last one.
 */

const COL_W = 64; // column diameter
const RY = 11; // ellipse squash (viewing angle)
const BASE_Y = 212;

const mix = (hex, amount, to = 0) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (s) => Math.round(((n >> s) & 255) * (1 - amount) + to * amount);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
};

function Column({ x, top, color, label, lit }) {
  const rx = COL_W / 2;
  return (
    <g>
      {/* body: front face from top ellipse down to base ellipse */}
      <path
        d={`M${x - rx},${top} L${x - rx},${BASE_Y} A${rx},${RY} 0 0 0 ${x + rx},${BASE_Y} L${x + rx},${top} Z`}
        fill={lit ? color : mix(color, 0.55)}
        style={{ transition: "fill .5s" }}
      />
      {/* edge shading */}
      <path
        d={`M${x - rx},${top} L${x - rx},${BASE_Y} A${rx},${RY} 0 0 0 ${x + rx},${BASE_Y} L${x + rx},${top} Z`}
        fill="url(#ps-shade)"
      />
      {/* top face */}
      <ellipse cx={x} cy={top} rx={rx} ry={RY} fill={lit ? mix(color, 0.35, 255) : mix(color, 0.25, 255)} style={{ transition: "fill .5s" }} />
      <text x={x} y={top + (BASE_Y - top) / 2 + 6} textAnchor="middle" fontSize="13" fontWeight="800" fill="#fff" opacity={lit ? 0.95 : 0.5} fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
        {label}
      </text>
    </g>
  );
}

function Trophy({ x, y, lit }) {
  return (
    <g transform={`translate(${x},${y})`} opacity={lit ? 1 : 0.35} style={{ transition: "opacity .5s" }}>
      <path d="M-11,-26 h22 v9 a11,11 0 0 1 -22,0 z" fill="#f5b63b" />
      <path d="M-11,-22 h-5 a5,5 0 0 0 5,8 M11,-22 h5 a5,5 0 0 1 -5,8" fill="none" stroke="#f5b63b" strokeWidth="2.5" />
      <rect x="-3" y="-8" width="6" height="6" fill="#c98a16" />
      <rect x="-9" y="-2" width="18" height="4" fill="#c98a16" />
      <ellipse cx="0" cy="2" rx="9" ry="2" fill="#000" opacity="0.15" />
    </g>
  );
}

export default function ProcessStairs({ steps, activeStep }) {
  const reduced = useReducedMotion();
  const n = steps.length;
  const gap = 84;
  const x0 = 40 + COL_W / 2;
  const colX = (i) => x0 + i * gap;
  const colTop = (i) => 176 - i * (120 / Math.max(1, n - 1));
  const current = Math.max(0, Math.min(activeStep, n - 1));
  const done = activeStep >= n - 1;
  const width = x0 + (n - 1) * gap + COL_W / 2 + 40;

  return (
    <svg viewBox={`0 0 ${width} 240`} className="w-full h-auto" role="img" aria-label={`Process: ${steps.map((s) => s.title).join(", ")}`}>
      <defs>
        <linearGradient id="ps-shade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#000" stopOpacity="0.3" />
          <stop offset="45%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.28" />
        </linearGradient>
        <linearGradient id="ps-gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* ground */}
      <ellipse cx={width / 2} cy={BASE_Y + 14} rx={width / 2 - 30} ry="8" fill="#000" opacity="0.25" />

      {/* dotted path over the tops */}
      <path
        d={steps.map((_, i) => `${i ? "L" : "M"}${colX(i)},${colTop(i) - 14}`).join(" ")}
        fill="none"
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="1.5"
        strokeDasharray="3 5"
      />

      {steps.map((s, i) => (
        <Column key={s.num} x={colX(i)} top={colTop(i)} color={s.accent} label={s.num} lit={activeStep >= i} />
      ))}

      <Trophy x={colX(n - 1)} y={colTop(n - 1) - 2} lit={done} />
      {done && !reduced && (
        <g>
          {[-22, 22, 0].map((dx, i) => (
            <motion.circle
              key={i}
              cx={colX(n - 1) + dx}
              cy={colTop(n - 1) - 34 - (i === 2 ? 14 : 0)}
              r="2.5"
              fill="#f5b63b"
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1.4, 0.5] }}
              transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.4 }}
            />
          ))}
        </g>
      )}

      {/* the app tile climbs to the active column (sits beside the trophy on the last) */}
      <motion.g
        initial={false}
        animate={{ x: colX(current) - 13 + (done ? -26 : 0), y: colTop(current) - 30 }}
        transition={reduced ? { duration: 0 } : { type: "spring", stiffness: 160, damping: 16 }}
      >
        <rect width="26" height="26" rx="7" fill="#2196F3" />
        <rect width="26" height="26" rx="7" fill="url(#ps-gloss)" />
        <text x="13" y="18" textAnchor="middle" fontSize="12" fontWeight="800" fill="#fff" fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
          app
        </text>
      </motion.g>
    </svg>
  );
}
